import React, { useMemo, useRef, useState, useEffect, Fragment, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import { MotifTooltip, MotifIcon } from '@ey-xd/motif-react';
import { actionIcInfoOutline24px } from '@ey-xd/motif-react/assets/icons';
import { AG_GRID_LICENSE_KEY } from '@constants/licenseKeys';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { columnDefsKeys } from './columnDefs';
import { columnDefsKeys2 } from './ColumnDefsInner';
import TableHeader from '@molecules/msl/components/forecast/tableHeader';
import { LicenseManager } from 'ag-grid-enterprise';
import '../tableComponent/styles/style.css';
import MslJudgeModal from '@organisms/modals/mslJudgeModal.jsx';
import { formateTOV, generateLoadingData } from '@util/commonUtil';
import { getLiveForecastAccounts, getLiveForecastAccountSegments } from '@services/mslServices';
import {
  Current_User,
  Msl_Opportunity_State,
  Active_Tab_Msl,
  System_Date_Time_State,
  Next_Button_Active_State,
  All_Month_MSL_Data_State,
  Msl_Opp_View_State,
} from '@recoil/recoil';
import {
  MSLHeadTable,
  TooltipContainer,
  TooltipIconWrapper,
  TooltipContent,
  TooltipStyle,
} from './mslTable.styles';
import {
  FORECAST_TABLE_CONST,
  SOURCE,
  SKELETON_LOADER_CONST,
  MSL_TOOLTIP_MESSAGES,
} from '@constants/commonConstants';
import { FooterTable } from '@atoms/msl/mslTable.styles';
import { mslFooterColumnDefs } from '@templates/msl/forecast/tableComponent/mslFooterColumnDef.js';
import { getMslGridData } from '@services/mslServices'; // eslint-disable-line
import { appInsights } from '@services/appInsightsService';
import { getOpportunitiesDue } from '@services/commonServices';
import { logError } from '@util/commonFunctions';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { MslFooterTableLoadingData, MslTopTableLoadingData } from '@data/mslSkeletonLoaderData';
import InnerTableHeader from '@molecules/msl/components/forecast/innerTableHeader';

LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY);
let gridApi; // eslint-disable-line
const MslTable = () => {
  // --- CRITICAL: Define the ref at the very top ---
  const topGrid = useRef(null);
  const bottomGrid = useRef(null);
  const innerGridStateRef = useRef({});
  const expandedSegmentsRef = useRef(new Set());
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%', overflowX: 'auto,' }), []);
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const [rowData, setRowData] = useState([]); // eslint-disable-line
  const [columnDefs, setColumnDefs] = useState(columnDefsKeys); // eslint-disable-line
  const currentUser = useRecoilValue(Current_User);
  const [mslRowFooterData, setMslRowFooterData] = useRecoilState(Msl_Opportunity_State);
  const tabActive = useRecoilValue(Active_Tab_Msl);
  const [showNowRowsToShowText, setShowNowRowsToShowText] = useState(true); // eslint-disable-line
  const systemDateTime = useRecoilValue(System_Date_Time_State);
  const setNextButtonActive = useSetRecoilState(Next_Button_Active_State);
  const [allMonthData, setAllMonthData] = useRecoilState(All_Month_MSL_Data_State);
  const [mslOppViewData, setMslOppViewData] = useRecoilState(Msl_Opp_View_State); // eslint-disable-line
  const [isLoading, setIsLoading] = useState(false); // eslint-disable-line
  const gridRef = useRef(null); // Reference to the grid
  const [headerSkeletonLoader, setHeaderSkeletonLoader] = useState(true);
  const [footerSkeletonLoader, setFooterSkeletonLoader] = useState(true);
  const [gridReady, setGridReady] = useState(false);
  const selectedUser = JSON.parse(localStorage.getItem('selectedUser'));
  const currentAccountId = selectedUser?.account_id;
  const [mslSalesGoalNotes, setMslSalesGoalNotes] = useState('');
  // --- Restore state from sessionStorage ONCE ---
  useEffect(() => {
    const saved = sessionStorage.getItem('msl_innerGridState');
    if (saved) {
      try {
        innerGridStateRef.current = JSON.parse(saved);
      } catch (e) {
        innerGridStateRef.current = {};
        console.log('[Init] Error parsing msl_innerGridState:', e);
      }
    }
    const expanded = sessionStorage.getItem('msl_expandedSegments');
    if (expanded) {
      try {
        expandedSegmentsRef.current = new Set(JSON.parse(expanded));
      } catch (e) {
        expandedSegmentsRef.current = new Set();
      }
    }
  }, []);
  // Save on unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('msl_innerGridState', JSON.stringify(innerGridStateRef.current));
      sessionStorage.setItem(
        'msl_expandedSegments',
        JSON.stringify([...expandedSegmentsRef.current]),
      );
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  const linkForaccountId = useRef([]);
  const linkForAccountSegments = useRef({});

  useEffect(() => {
    const handleResize = () => {
      if (gridRef.current) {
        gridRef.current.api.sizeColumnsToFit();
      }
    };

    // Initial column resizing
    handleResize();

    // Resize event listener
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [gridRef]);

  //helper function for fetching account segment and footer api data

  const getOpportunitiesAndRollingData = async (currentUser, systemDateTime, tabActive) => {
    try {
      const opportunitiesDueAPI = await getOpportunitiesDue(
        currentUser?.role,
        currentUser?.email,
        systemDateTime.split('T')[0],
      );

      const MSLData = JSON.parse(localStorage.getItem('selectedUser'));
      const EMAIL = currentUser?.email;
      const SUBMITTEDYEAR = opportunitiesDueAPI?.fiscal_year;
      const MSLID = MSLData?.account_id;
      const SUBMITTEDMONTH = opportunitiesDueAPI?.month;

      let ROLLINGMONTH, ROLLINGYEAR, ROLLINGQUARTER;

      if (tabActive === 0) {
        ROLLINGMONTH = opportunitiesDueAPI?.month;
        ROLLINGYEAR = opportunitiesDueAPI?.fiscal_year;
        ROLLINGQUARTER = opportunitiesDueAPI?.quarter;
      } else if (tabActive === 1) {
        ROLLINGMONTH = opportunitiesDueAPI?.next_month;
        ROLLINGYEAR = opportunitiesDueAPI?.next_month_fiscal_year;
        ROLLINGQUARTER = opportunitiesDueAPI?.next_month_quarter;
      } else {
        ROLLINGMONTH = opportunitiesDueAPI?.next2next_month;
        ROLLINGYEAR = opportunitiesDueAPI?.next2next_month_fiscal_year;
        ROLLINGQUARTER = opportunitiesDueAPI?.next2next_month_quarter;
      }

      return {
        EMAIL,
        SUBMITTEDYEAR,
        MSLID,
        SUBMITTEDMONTH,
        ROLLINGMONTH,
        ROLLINGYEAR,
        ROLLINGQUARTER,
        enableSalesGoal: opportunitiesDueAPI?.enableSalesGoal,
        mslSalesGoalNotes: opportunitiesDueAPI?.mslSalesGoalNotes,
      };
    } catch (error) {
      logError('getOpportunitiesAndRollingData', error);
      throw error; // Re-throw the error to handle it in the calling function
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setHeaderSkeletonLoader(true);
      try {
        const { EMAIL, SUBMITTEDYEAR, MSLID, SUBMITTEDMONTH, ROLLINGMONTH, ROLLINGYEAR } =
          await getOpportunitiesAndRollingData(currentUser, systemDateTime, tabActive);

        const getOpportunitiesDetailsAPI = await getLiveForecastAccountSegments(
          EMAIL,
          MSLID,
          SUBMITTEDMONTH,
          SUBMITTEDYEAR,
          ROLLINGYEAR,
          ROLLINGMONTH,
          JSON.parse(localStorage.getItem('isReadOnly')),
        );

        setShowNowRowsToShowText(!showNowRowsToShowText);
        const newData = [...getOpportunitiesDetailsAPI];
        setHeaderSkeletonLoader(false);
        setRowData(newData);

        if (newData.length !== 0) {
          setNextButtonActive(true);
        }
      } catch (error) {
        appInsights.trackTrace({
          message: JSON.stringify({
            message: `Error Fetching First API data for Accounts Segments: ${error}  `,
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 3, // 3 represents failure
        });
        setRowData([]);
        setHeaderSkeletonLoader(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentUser, systemDateTime, tabActive]);

  //useEffect for retaining the data on tab change for allMonthData
  useEffect(() => {
    if (!Array.isArray(allMonthData[tabActive])) {
      setAllMonthData(prevState => ({
        ...prevState,
        [tabActive]: [],
      }));
    }
  }, [tabActive, allMonthData, setAllMonthData]);
  //useEffect to fetch api response of account segment and footer tables
  useEffect(() => {
    setRowData([]);
    setMslRowFooterData([]);
    setShowNowRowsToShowText(showNowRowsToShowText);
    // Ensure required dependencies are valid before making API calls
    if (!currentUser || !systemDateTime || tabActive === null || tabActive < 0 || tabActive > 2) {
      console.log('Skipping API calls due to invalid state:', {
        currentUser,
        systemDateTime,
        tabActive,
      });
      return;
    }

    const fetchMslFooterData = async () => {
      setFooterSkeletonLoader(true);

      try {
        const {
          EMAIL,
          SUBMITTEDYEAR,
          MSLID,
          SUBMITTEDMONTH,
          ROLLINGMONTH,
          ROLLINGYEAR,
          ROLLINGQUARTER,
          enableSalesGoal,
          mslSalesGoalNotes,
        } = await getOpportunitiesAndRollingData(currentUser, systemDateTime, tabActive);

        // Update sales goal notes for display
        // Only display if enableSalesGoal is explicitly 'true' and message exists
        if (enableSalesGoal === 'true' && mslSalesGoalNotes && mslSalesGoalNotes.trim()) {
          setMslSalesGoalNotes(mslSalesGoalNotes);
        } else {
          setMslSalesGoalNotes('');
        }

        const data = await getMslGridData(
          EMAIL,
          ROLLINGMONTH,
          ROLLINGYEAR,
          SUBMITTEDMONTH,
          SUBMITTEDYEAR,
          MSLID,
          JSON.parse(localStorage.getItem('isReadOnly')),
        );

        if (data && data.length > 0) {
          const innerAGData = Object.keys(data[0]).map(key => ({
            category: data[0][key]?.category,
            assurance: data[0][key]?.assurance,
            consulting: data[0][key]?.consulting,
            sat: data[0][key]?.sat,
            tax: data[0][key]?.tax,
            itts: data[0][key]?.itts,
            emeia: data[0][key]?.emeia,
            total: data[0][key]?.total,
            month: ROLLINGMONTH,
            quarter: ROLLINGQUARTER,
            year: ROLLINGYEAR,
          }));

          setFooterSkeletonLoader(false);

          if (
            innerAGData.length !== 0 &&
            allMonthData[tabActive]?.your_judge === null &&
            allMonthData[tabActive]?.your_total_commit === null
          ) {
            setMslRowFooterData(innerAGData);
            const recoilAllMonthData = { ...allMonthData };
            recoilAllMonthData[tabActive] = innerAGData;

            setAllMonthData(recoilAllMonthData);
            setMslOppViewData(recoilAllMonthData);
          } else {
            const recoilAllMonthData = { ...allMonthData };
            setMslRowFooterData(recoilAllMonthData[tabActive]);
          }
        }
      } catch (error) {
        appInsights.trackTrace({
          message: JSON.stringify({
            message: `Error Fetching Footer API data for Footer Table: ${error}  `,
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 3, // 3 represents failure
        });
        setMslRowFooterData([]);
        setFooterSkeletonLoader(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMslFooterData();
  }, [currentUser, systemDateTime, tabActive]);

  useEffect(() => {
    // Restore expanded segments
    const expanded = JSON.parse(sessionStorage.getItem('msl_expandedSegments') || '[]');
    expandedSegmentsRef.current = new Set(expanded);

    // Restore filters
    const saved = JSON.parse(sessionStorage.getItem('msl_innerGridState') || '{}');
    innerGridStateRef.current = saved;
  }, [currentAccountId]);

  useEffect(() => {
    if (
      topGrid.current &&
      topGrid.current.api &&
      expandedSegmentsRef.current.size > 0 &&
      gridReady
    ) {
      setTimeout(() => {
        expandedSegmentsRef.current.forEach(combinedKey => {
          const rowNode = topGrid.current.api.getRowNode(combinedKey);
          if (rowNode && !rowNode.expanded) {
            rowNode.setExpanded(true);
          }
        });
      }, 0);
    }
  }, [rowData, headerSkeletonLoader, gridReady]);

  //to get the grid ready on data load
  const handleGridReady = params => {
    gridApi = params.api; // Store globally
    setGridReady(true);
  };

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      enableColumnMenu: false, // Enable column menu for all columns
      flex: 1,
    };
  }, []);

  //to navigate to CRM page on click of accounts link
  const LinkRenderer = ({ value, segmentKey }) => {
    const handleClick = event => {
      event.preventDefault();

      if (!segmentKey) {
        appInsights.trackTrace({
          message: JSON.stringify({
            message: 'Segment key is undefined. Cannot fetch data for the segment.',
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 3, // 3 represents failure
        });
        return;
      }

      const segmentData = linkForAccountSegments.current[segmentKey];

      if (segmentData) {
        const clickedAccount = segmentData.find(item => item.account_name === value);

        if (clickedAccount) {
          if (
            localStorage.getItem('isSuperUser') === 'true' &&
            localStorage.getItem('targetUserEmail') !== 'null'
          ) {
            // MSL new tab window operation in super user mode - use userEmail for operations
            localStorage.setItem('isMslNewTabOperation', 'true');
            const accountId = clickedAccount.accountid;
            const url = `${process.env.REACT_APP_OPPS_NAME_IFRAME_URL}/main.aspx?appid=${process.env.REACT_APP_OPP_NAME_URL_APP_ID}&forceUCI=1&pagetype=entityrecord&etn=account&id=${accountId}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          } else {
            // Regular user operation - MSL always opens in new tab window
            // they use userEmail via headers in TokenInterceptor
            localStorage.setItem('isMslNewTabOperation', 'false');
            const accountId = clickedAccount.accountid;
            const url = `${process.env.REACT_APP_OPPS_NAME_IFRAME_URL}/main.aspx?appid=${process.env.REACT_APP_OPP_NAME_URL_APP_ID}&forceUCI=1&pagetype=entityrecord&etn=account&id=${accountId}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        } else {
          appInsights.trackTrace({
            message: JSON.stringify({
              message: `No matching account found for the clicked account name: ${value}.`,
              userId: localStorage.getItem('userEmail'),
              source: SOURCE.FRONTEND,
            }),
            severityLevel: 3, // 3 represents failure
          });
        }
      } else {
        appInsights.trackTrace({
          message: JSON.stringify({
            message: `No data found for the segment: ${segmentKey}`,
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 3, // 3 represents failure
        });
      }
    };

    return value ? (
      <button
        type="button"
        onClick={handleClick}
        style={{
          textDecoration: 'none',
          color: '#155cb4',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        {value}
      </button>
    ) : null;
  };

  //calls the second api on expand of the account segment and controls the inner accounts
  const handleAccordionToggle = async params => {
    const accountSegmentName = params?.data?.account_segment; // Extract the segment key

    if (!accountSegmentName) {
      appInsights.trackTrace({
        message: JSON.stringify({
          message: 'Account segment name is undefined.',
          userId: localStorage.getItem('userEmail'),
          source: SOURCE.FRONTEND,
        }),
        severityLevel: 3, // 3 represents failure
      });

      return;
    }

    const detailData = await fetchSecondApiData(accountSegmentName, tabActive);

    const formattedData = detailData.map(item => ({
      ...item,
      gcsp: item?.gcsp_name,
      emiaacr: item?.emeia_acr,
      name: item?.account_name,
      accountid: item?.accountid,
      segmentKey: accountSegmentName, // Include the segmentKey in the row data
    }));

    linkForAccountSegments.current[accountSegmentName] = formattedData;

    params.successCallback(formattedData);

    setTimeout(() => {
      if (params.api && typeof params.api.getDetailGridInfo === 'function') {
        const innerGrid = params.api.getDetailGridInfo(params.node.id);
        if (innerGrid) {
          const combinedKey = `${currentAccountId}_${accountSegmentName}`;
          const savedState = innerGridStateRef.current[combinedKey];
          if (savedState) {
            innerGrid.api.setFilterModel(savedState.filterModel || []);
          }
        }
      }
    }, 0);
  };

  const SkeletonLoader = props => {
    const columnCount = props?.columnApi?.getAllDisplayedColumns().length;
    const rowCount = 5;
    return (
      <div style={{ padding: '10px', width: '100%' }}>
        {[...Array(rowCount)].map((_, rowIndex) => (
          <div key={rowIndex} style={{ gap: '10px', marginBottom: '15px' }}>
            {[...Array(columnCount)].map((_, colIndex) => (
              <Skeleton key={colIndex} width="95vw" />
            ))}
          </div>
        ))}
      </div>
    );
  };

  const defaultInnerColDef = useMemo(() => {
    return {
      sortable: true, // Enable sorting globally
      filter: true, // Enable filtering globally
      enableColumnMenu: false, // Disable the column menu globally
      flex: 1, // Allow columns to flex and fill available space
    };
  }, []);

  // detail cell renderer params for the inner grid
  const detailCellRendererParams = useCallback(
    params => {
      // Get the segment key for this detail grid instance
      const segmentKey = params.data.account_segment;

      return {
        detailGridOptions: {
          masterDetail: false,
          pagination: true,
          defaultColDef: defaultInnerColDef,
          suppressAggFuncInHeader: true,
          paginationPageSize: 5,
          columnDefs: columnDefsKeys2,
          loadingOverlayComponent: SkeletonLoader,
          context: { segmentKey }, // Pass segmentKey for this detail grid instance!
          autoGroupColumnDef: {
            headerName: 'Accounts',
            defaultColDef: defaultInnerColDef,
            tooltipValueGetter: params => {
              if (!params?.value) return null;
              return `${params.value}`;
            },
            cellStyle: { display: 'flex', justifyContent: 'flex-start' },
            filter: 'agTextColumnFilter',
            filterValueGetter: params => {
              // For grouped rows, return the group key or a placeholder
              if (params.node.group) {
                return params.node.key; // Use the group key for filtering grouped rows
              }
              // For non-grouped rows, return the account name
              return params.data?.name || ''; // Ensure it returns an empty string if name is undefined
            },
            suppressMovable: true,
            cellRenderer: 'agGroupCellRenderer',
            headerComponent: InnerTableHeader,
            headerComponentParams: { enableSorting: true },
            cellRendererParams: params => {
              // Find the segmentKey dynamically based on the account name
              let segmentKey = null;
              for (const key in linkForAccountSegments.current) {
                const segmentData = linkForAccountSegments.current[key];
                if (segmentData.some(item => item.account_name === params.value)) {
                  segmentKey = key; // Set the segmentKey if the account name matches
                  break;
                }
              }

              return {
                innerRenderer: LinkRenderer,
                suppressCount: true,
                accountid: params?.data?.accountid, // Pass the account ID
                segmentKey, // Pass the dynamically determined segmentKey
              };
            },
            minWidth: 170,
            hide: window.innerWidth < 600,
          },
          onFirstDataRendered: detailParams => {
            // Use the segmentKey from this detail grid's context
            const segmentKey = detailParams?.context?.segmentKey;

            if (!segmentKey) {
              return;
            }
            const combinedKey = `${currentAccountId}_${segmentKey}`;
            const savedState = innerGridStateRef.current[combinedKey];

            if (savedState) {
              setTimeout(() => {
                detailParams.api.setFilterModel(savedState.filterModel || {});
                detailParams.api.refreshClientSideRowModel?.('sort');
              }, 0);
            }

            const persistState = () => {
              // Expanded segments
              const expandedSegments = JSON.parse(
                sessionStorage.getItem('msl_expandedSegments') || '{}',
              );
              expandedSegments[combinedKey] = [...expandedSegmentsRef.current];
              sessionStorage.setItem('msl_expandedSegments', JSON.stringify(expandedSegments));

              // Filters
              const innerGridState = JSON.parse(
                sessionStorage.getItem('msl_innerGridState') || '{}',
              );
              innerGridState[combinedKey] = innerGridStateRef.current[combinedKey];
              sessionStorage.setItem('msl_innerGridState', JSON.stringify(innerGridState));
            };

            const saveState = () => {
              const filterModel = detailParams.api.getFilterModel() || {};
              const combinedKey = `${currentAccountId}_${segmentKey}`;
              innerGridStateRef.current[combinedKey] = { filterModel };
              persistState();
            };

            detailParams.api.addEventListener('sortChanged', saveState);
            detailParams.api.addEventListener('filterChanged', saveState);
          },
        },
        getDetailRowData: handleAccordionToggle,
      };
    },
    [columnDefsKeys2, tabActive],
  );

  //cell renderer for the footer table
  const MSL_CellRenderer = ({ value, data, colDef, KeyName }) => {
    if (value == SKELETON_LOADER_CONST.LOADING) {
      return <Skeleton width={150} />;
    }
    // Utility function to truncate decimals
    const truncateDecimals = num => {
      if (num != null && !isNaN(num)) {
        return Math.floor(num); // Truncate decimals
      }
      return 0; // Default to 0 if the value is invalid
    };

    // Truncate specific fields from the backend
    const truncatedData = {
      ...data,
      assurance: truncateDecimals(data?.assurance),
      consulting: truncateDecimals(data?.consulting),
      sat: truncateDecimals(data?.sat),
      tax: truncateDecimals(data?.tax),
      itts: truncateDecimals(data?.itts),
      emeia: truncateDecimals(data?.emeia), // Or emiaacr if that's the correct field
    };

    // Map category to key
    const keyByCategory = item => {
      const categoryMap = {
        'Monthly Sales Goal': 'monthly_sales_goal',
        'Pursuit Leader Commits + Audit/TR': 'pl_commits_audit_tech_risk',
        'GCSP Judge': 'gcsp_commits',
        'Total Commit (PL Commit + Audit/TR + GCSP Judge)': 'total_committed',
        'Rolling 3 Fiscal Period Forecast Accuracy (PL + Audit/TR + GCSP)': 'pl_gcsp',
        'Your Judge': 'your_judge',
        'Your Total Commit': 'your_total_commit',
      };
      return categoryMap[item];
    };

    const keyNameC = keyByCategory(data.category);

    // Existing logic for PL_GCSP
    if (keyNameC === FORECAST_TABLE_CONST.PL_GCSP) {
      return `${value}%`;
    }

    // Existing logic for "Your Judge"
    if (keyNameC === FORECAST_TABLE_CONST.YOUR_JUDGE) {
      if (KeyName === 'Total') {
        // Use truncated values to calculate the total
        const fieldsToSum = ['assurance', 'consulting', 'tax', 'sat', 'itts', 'emeia'];
        const yourJudgeTotal = fieldsToSum.reduce(
          (acc, field) => acc + (truncatedData[field] || 0),
          0,
        );
        return formateTOV(yourJudgeTotal) || '$0';
      }

      // Render modal for individual fields
      const totalCommitted = truncatedData.total_committed?.[colDef.field] || 0;
      return (
        <MslJudgeModal
          KeyName={KeyName}
          mslRowFooterData={mslRowFooterData}
          totalCommitted={totalCommitted}
        />
      );
    }
    // Default rendering logic
    return formateTOV(truncateDecimals(value));
  };

  const MSL_catagory_CellRenderer = ({ value }) => {
    if (value === 'Monthly Sales Goal') {
      // Append '**' to the value if it matches 'Monthly Sales Goal'
      return `${value}**`;
    }
    if (value == SKELETON_LOADER_CONST.LOADING) {
      return <Skeleton width="50%" />;
    }

    // Add tooltip for Pursuit Leader Commits + Audit/TR
    if (value === 'Pursuit Leader Commits + Audit/TR') {
      return (
        <TooltipContainer>
          <span>{value}</span>
          <MotifTooltip
            trigger={
              <TooltipIconWrapper>
                <MotifIcon src={actionIcInfoOutline24px} style={{ color: '#4682b4' }} />
              </TooltipIconWrapper>
            }
            allowHover={true}
            allowClick={true}
            placement="top"
            hideCloseButton={true}
            style={TooltipStyle}
          >
            <TooltipContent>{MSL_TOOLTIP_MESSAGES.PURSUIT_LEADER_COMMITS_AUDIT_TR}</TooltipContent>
          </MotifTooltip>
        </TooltipContainer>
      );
    }

    return value;
  };

  //style for the footer table
  const getFooterTableRowStyle = params => {
    if (params.data.category === FORECAST_TABLE_CONST.YOUR_TOTAL_COMMIT) {
      return { background: params?.data?.background, fontWeight: 'bold' };
    }
    return { background: params?.data?.background };
  };

  //fetches the second api of accounts data on expand of the account segment
  const fetchSecondApiData = async (accountSegmentName, tabActive) => {
    try {
      const opportunitiesDueAPI = await getOpportunitiesDue(
        currentUser.role,
        currentUser.email,
        systemDateTime.split('T')[0],
      );

      const MslData = JSON.parse(localStorage.getItem('selectedUser'));
      const MSLID = MslData.account_id;
      const EMAIL = currentUser?.email;
      const SEGMENT = accountSegmentName;
      const NEXT = 500;
      const OFFSET = 0;

      let ROLLINGMONTH, ROLLINGFISCALYEAR;

      // Determine ROLLINGMONTH and ROLLINGFISCALYEAR based on tabActive
      if (tabActive === 0) {
        ROLLINGMONTH = opportunitiesDueAPI?.month;
        ROLLINGFISCALYEAR = opportunitiesDueAPI?.fiscal_year;
      } else if (tabActive === 1) {
        ROLLINGMONTH = opportunitiesDueAPI?.next_month;
        ROLLINGFISCALYEAR = opportunitiesDueAPI?.next_month_fiscal_year;
      } else {
        ROLLINGMONTH = opportunitiesDueAPI?.next2next_month;
        ROLLINGFISCALYEAR = opportunitiesDueAPI?.next2next_month_fiscal_year;
      }

      const getOpportunitiesDetailsAPI = await getLiveForecastAccounts(
        MSLID,
        opportunitiesDueAPI?.month, // SUBMITTEDMONTH
        opportunitiesDueAPI?.fiscal_year, // SUBMITTEDFISCALYEAR
        ROLLINGFISCALYEAR,
        ROLLINGMONTH,
        EMAIL,
        SEGMENT,
        NEXT,
        OFFSET,
        JSON.parse(localStorage.getItem('isReadOnly')),
      );

      setShowNowRowsToShowText(!showNowRowsToShowText);
      setNextButtonActive(true);
      const modifiedData = getOpportunitiesDetailsAPI.map(item => ({
        ...item,
        account_name: item?.account_name,
        accountid: item?.accountid, // Include accountid in the data
      }));

      const sortedData = modifiedData.sort((a, b) => {
        if (a.value_type > b.value_type) return -1;
        if (a.value_type < b.value_type) return 1;
        return 0;
      });

      linkForaccountId.current = sortedData;

      return sortedData; // Return the sorted data
    } catch (error) {
      logError('fetchSecondApiData', error);
      return []; // Return an empty array in case of an error
    }
  };

  //this will be required on rendering the first cell, cannot delete
  //function for the render of the table on first load
  // const onFirstDataRendered = useCallback(params => {
  //   setTimeout(() => {
  //     params.api.getDisplayedRowAtIndex(1).setExpanded(true);
  //   }, 0);
  // }, []);

  //function to check if the row is master or not
  const isRowMaster = obj => {
    const isMaster = ['Core', 'Inbound'].includes(obj.account_segment);
    return isMaster ? false : true;
  };

  //jsx element for the table
  return (
    <div className='"msl-table-container"'>
      <MSLHeadTable
        className="ag-theme-alpine "
        id="mslheadtable"
        style={{
          width: '100%',
        }}
      >
        <div style={containerStyle}>
          <div style={gridStyle}>
            <AgGridReact
              ref={topGrid}
              alignedGrids={[bottomGrid]}
              rowData={headerSkeletonLoader ? generateLoadingData(MslTopTableLoadingData) : rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              domLayout="autoHeight"
              masterDetail={true} // Enable master-detail functionality
              isRowMaster={isRowMaster}
              detailCellRendererParams={detailCellRendererParams}
              onGridReady={handleGridReady}
              // onFirstDataRendered={onFirstDataRendered}
              components={{ agColumnHeader: TableHeader }}
              onRowGroupOpened={params => {
                const segmentKey = params.node.id;
                const combinedKey = `${currentAccountId}_${segmentKey}`;

                if (params.node.expanded) {
                  expandedSegmentsRef.current.add(combinedKey);
                } else {
                  expandedSegmentsRef.current.delete(combinedKey);
                }
                sessionStorage.setItem(
                  'msl_expandedSegments',
                  JSON.stringify([...expandedSegmentsRef.current]),
                );
              }}
              autoGroupColumnDef={{
                headerName: 'Segment',
                width: 400,
                suppressSizeToFit: true,
                cellRendererParams: {
                  suppressCount: true,
                },
              }}
            />
          </div>
        </div>
      </MSLHeadTable>
      <Fragment style={{ width: '100vw', bottom: 0 }}>
        <FooterTable className="ag-theme-alpine" id="mslfootertable">
          {/* {footerSkeletonLoader ? (
            <Skeleton width="100%" height={30} /> // Skeleton loader for the div
          ) : process.env.REACT_APP_MSL_ENABLE_SALESGOAL_NOTES === 'true' ? (
            <div className="msl-pinned-text">{process.env.REACT_APP_MSL_SALESGOAL_NOTES}</div>
          ) : null} */}
          {footerSkeletonLoader ? (
            <Skeleton width="100%" height={30} />
          ) : mslSalesGoalNotes ? (
            <div className="msl-pinned-text">{mslSalesGoalNotes}</div>
          ) : null}
          <AgGridReact
            ref={bottomGrid}
            alignedGrids={[topGrid]}
            columnDefs={mslFooterColumnDefs}
            getRowStyle={getFooterTableRowStyle}
            rowData={
              footerSkeletonLoader
                ? generateLoadingData(MslFooterTableLoadingData)
                : mslRowFooterData
            }
            defaultColDef={defaultColDef}
            domLayout="autoHeight"
            components={{
              msl_CellRenderer: MSL_CellRenderer,
              msl_catagory_CellRenderer: MSL_catagory_CellRenderer,
            }}
          />
        </FooterTable>
      </Fragment>
    </div>
  );
};

export default MslTable;
